// Detection for the AI tutor's inline diagram marker `[DIAGRAM: <description>]`.
//
// The chat model emits this bracket token (same family as the existing `[IMAGE-N]` /
// `[INFOGRAPHIC-N]` tokens) when a drawing would help teaching; the frontend replaces a
// COMPLETE marker with a rendered image (see AiDiagram). This split MUST run BEFORE the
// `constructInputWithSpecialExpressionList` tokenizer so the description text (which may
// contain `$$`/`**`/backticks) never reaches — and is never mangled by — that tokenizer.

export type DiagramSegment =
  | { type: "text"; value: string }
  | { type: "diagram"; description: string; complete: boolean };

const MARKER_START = "[DIAGRAM:";

// Upper bound on the description we carry (and later POST to the backend). A legit tutoring
// description is a short single line; this only bounds a crafted/oversized marker so a huge
// `[DIAGRAM: <very long…>]` can't produce a giant outbound request. The backend enforces its
// own (tighter) limit and is the real authority — this is a client-side belt-and-suspenders.
const MAX_DIAGRAM_DESCRIPTION = 2000;

const clampDescription = (raw: string): string =>
  raw.trim().slice(0, MAX_DIAGRAM_DESCRIPTION);

/**
 * Split `text` into ordered text/diagram segments. A closed `[DIAGRAM: …]` becomes a
 * `complete: true` diagram segment; a trailing unclosed `[DIAGRAM:` (still streaming) becomes
 * `complete: false` (render a placeholder, do not compile yet). Text with no marker returns a
 * single text segment unchanged (fast path — keeps all ordinary messages byte-identical).
 *
 * The marker's end is found by **balanced-bracket matching**, not "first `]`", so a `]` that
 * appears INSIDE the description — common in math, e.g. an interval `[a, b]` — no longer
 * truncates the marker and leak the remainder as stray text. Depth starts at 1 for the marker's
 * own opening `[`; the terminator is the `]` that brings depth back to 0. This is a single
 * linear scan (no regex backtracking / ReDoS).
 *
 * A COMPLETE marker with an empty/whitespace-only description (`[DIAGRAM: ]`) is dropped
 * entirely (emits nothing) rather than becoming a diagram segment — there is nothing to draw,
 * and emitting it would otherwise strand a permanent "drawing…" placeholder downstream. An
 * OPEN (still-streaming) marker keeps its placeholder even while empty, since the description
 * may still be arriving.
 */
export const splitDiagramSegments = (text: string): DiagramSegment[] => {
  if (!text) return [];
  if (text.indexOf(MARKER_START) === -1) {
    return [{ type: "text", value: text }];
  }

  const segments: DiagramSegment[] = [];
  const pushText = (value: string) => {
    if (value) segments.push({ type: "text", value });
  };

  let cursor = 0;
  while (cursor < text.length) {
    const start = text.indexOf(MARKER_START, cursor);
    if (start === -1) {
      pushText(text.slice(cursor));
      break;
    }
    pushText(text.slice(cursor, start));

    // Walk the description with balanced brackets. depth = 1 accounts for the marker's own '['.
    const descStart = start + MARKER_START.length;
    let depth = 1;
    let end = descStart;
    for (; end < text.length; end++) {
      const ch = text[end];
      if (ch === "[") {
        depth++;
      } else if (ch === "]") {
        depth--;
        if (depth === 0) break;
      }
    }

    if (depth === 0) {
      // Balanced close — text[end] is the matching ']'.
      const description = clampDescription(text.slice(descStart, end));
      if (description) {
        segments.push({ type: "diagram", description, complete: true });
      }
      cursor = end + 1;
      continue;
    }

    // Balanced scan hit end-of-text without closing: the description has an UNBALANCED '[' —
    // a half-open interval ([0, 1) / (a, b]), an array index, a \left[ — exactly the math this
    // feature targets. Fall back to the FIRST ']' after the marker as the close, so a genuinely
    // CLOSED marker still ends (and its trailing text is preserved) instead of being mis-read as
    // streaming, which would strand a permanent placeholder AND drop the trailing prose. Only when
    // NO ']' follows at all is the marker really still streaming.
    const fallbackClose = text.indexOf("]", descStart);
    if (fallbackClose !== -1) {
      const description = clampDescription(text.slice(descStart, fallbackClose));
      if (description) {
        segments.push({ type: "diagram", description, complete: true });
      }
      cursor = fallbackClose + 1;
    } else {
      segments.push({
        type: "diagram",
        description: clampDescription(text.slice(descStart)),
        complete: false,
      });
      break;
    }
  }

  return segments;
};
